<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

export interface NavMenuItem {
  CodigoOpcion: number;
  CodigoOpcionPadre: number | null;
  Titulo: string;
  Url: string;
  Icono: string;
  Tooltip: string;
  TipoOpcion: string;
  Orden: number;
  NumeroOperacion: number;
  NombreOperacion: string;
  DescripcionOperacion: string;
  Visible: boolean;
}

interface TreeNode {
  item: NavMenuItem;
  children: TreeNode[];
  isOpen: boolean;
}

const props = defineProps<{
  items: NavMenuItem[];
  activeUrl?: string;
  title?: string;
}>();

const emit = defineEmits<{
  navigate: [url: string];
}>();

const collapsed = ref(false);
const mobileOpen = ref(false);
const isMobile = ref(false);
const svgCache = new Map<string, string>();
const iconSvgs = ref<Record<number, string>>({});
const openGroups = ref<Set<number>>(new Set());

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768;
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});

watch(
  () => props.items,
  () => {
    openGroups.value = new Set();
    void loadIcons();
  },
  { immediate: true },
);

async function fetchSvg(url: string): Promise<string> {
  if (!url) return '';
  if (svgCache.has(url)) return svgCache.get(url)!;
  try {
    const res = await fetch(url);
    if (!res.ok) return '';
    const svg = await res.text();
    svgCache.set(url, svg);
    return svg;
  } catch {
    return '';
  }
}

async function loadIcons() {
  for (const item of props.items) {
    if (item.Icono && !iconSvgs.value[item.CodigoOpcion]) {
      const svg = await fetchSvg(item.Icono);
      if (svg) iconSvgs.value = { ...iconSvgs.value, [item.CodigoOpcion]: svg };
    }
  }
}

const ALLOWED_TIPO_OPCION = new Set(['M', 'G']);

const tree = computed<TreeNode[]>(() => {
  const visible = props.items
    .filter(i => i.Visible && ALLOWED_TIPO_OPCION.has(i.TipoOpcion))
    .sort((a, b) => a.Orden - b.Orden);
  const map = new Map<number, TreeNode>();

  for (const item of visible) {
    map.set(item.CodigoOpcion, { item, children: [], isOpen: false });
  }

  const roots: TreeNode[] = [];
  for (const item of visible) {
    const node = map.get(item.CodigoOpcion)!;
    if (item.CodigoOpcionPadre === null) {
      roots.push(node);
    } else {
      const parent = map.get(item.CodigoOpcionPadre);
      if (parent) parent.children.push(node);
    }
  }
  return roots;
});

function isActive(url: string): boolean {
  if (!url) return false;
  const current = props.activeUrl || window.location.pathname;
  return current === url || current.startsWith(url + '/');
}

function hasActiveChild(node: TreeNode): boolean {
  return node.children.some(c => isActive(c.item.Url) || hasActiveChild(c));
}

function toggleGroup(code: number) {
  if (openGroups.value.has(code)) {
    openGroups.value.delete(code);
  } else {
    openGroups.value.add(code);
  }
  openGroups.value = new Set(openGroups.value);
}

function isGroupOpen(code: number): boolean {
  return openGroups.value.has(code);
}

function handleNavigate(url: string) {
  if (!url) return;
  mobileOpen.value = false;
  emit('navigate', url);
}

function toggleCollapse() {
  collapsed.value = !collapsed.value;
}

function openMobileMenu() {
  mobileOpen.value = true;
}

function closeMobileMenu() {
  mobileOpen.value = false;
}
</script>

<template>
  <!-- Botón hamburger → teleportado al header del layout en mobile -->
  <Teleport to="#mf-menu-trigger">
    <button
      v-if="isMobile"
      class="nav-hamburger"
      aria-label="Abrir menú de navegación"
      @click="openMobileMenu"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </svg>
    </button>
  </Teleport>

  <!-- Overlay mobile -->
  <Teleport v-if="mobileOpen" to="body">
    <div class="nav-overlay" role="dialog" aria-modal="true" aria-label="Menú de navegación" @click.self="closeMobileMenu">
      <aside class="nav-sidebar nav-sidebar--mobile">
        <div class="nav-header">
          <span class="nav-title">{{ title || 'Portal QA' }}</span>
          <button class="nav-close-btn" aria-label="Cerrar menú" @click="closeMobileMenu">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <nav class="nav-list" aria-label="Navegación principal">
          <template v-for="node in tree" :key="node.item.CodigoOpcion">
            <!-- Grupo con hijos -->
            <div v-if="node.children.length > 0" class="nav-group">
              <button
                class="nav-group-header"
                :class="{ 'nav-group-header--active': hasActiveChild(node) }"
                :title="node.item.Tooltip"
                @click="toggleGroup(node.item.CodigoOpcion)"
              >
                <span class="nav-icon" aria-hidden="true">
                  <span v-if="iconSvgs[node.item.CodigoOpcion]" v-html="iconSvgs[node.item.CodigoOpcion]" class="nav-icon-svg" />
                  <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                    <circle cx="12" cy="12" r="9" />
                  </svg>
                </span>
                <span class="nav-label">{{ node.item.Titulo }}</span>
                <svg
                  class="nav-chevron"
                  :class="{ 'nav-chevron--open': isGroupOpen(node.item.CodigoOpcion) }"
                  width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              <div v-show="isGroupOpen(node.item.CodigoOpcion)" class="nav-children">
                <template v-for="child in node.children" :key="child.item.CodigoOpcion">
                  <!-- Nietos -->
                  <div v-if="child.children.length > 0" class="nav-group nav-group--nested">
                    <button
                      class="nav-group-header nav-group-header--nested"
                      :class="{ 'nav-group-header--active': hasActiveChild(child) }"
                      :title="child.item.Tooltip"
                      @click="toggleGroup(child.item.CodigoOpcion)"
                    >
                      <span class="nav-label nav-label--nested">{{ child.item.Titulo }}</span>
                      <svg
                        class="nav-chevron"
                        :class="{ 'nav-chevron--open': isGroupOpen(child.item.CodigoOpcion) }"
                        width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                    <div v-show="isGroupOpen(child.item.CodigoOpcion)" class="nav-children nav-children--nested">
                      <button
                        v-for="grandchild in child.children"
                        :key="grandchild.item.CodigoOpcion"
                        class="nav-item nav-item--grandchild"
                        :class="{ 'nav-item--active': isActive(grandchild.item.Url) }"
                        :title="grandchild.item.Tooltip"
                        @click="handleNavigate(grandchild.item.Url)"
                      >
                        <span class="nav-label nav-label--nested">{{ grandchild.item.Titulo }}</span>
                      </button>
                    </div>
                  </div>
                  <!-- Hijo hoja -->
                  <button
                    v-else
                    class="nav-item nav-item--child"
                    :class="{ 'nav-item--active': isActive(child.item.Url) }"
                    :title="child.item.Tooltip"
                    @click="handleNavigate(child.item.Url)"
                  >
                    <span class="nav-label nav-label--nested">{{ child.item.Titulo }}</span>
                  </button>
                </template>
              </div>
            </div>
            <!-- Item raíz sin hijos (link directo) -->
            <button
              v-else
              class="nav-item nav-item--root"
              :class="{ 'nav-item--active': isActive(node.item.Url) }"
              :title="node.item.Tooltip"
              @click="handleNavigate(node.item.Url)"
            >
              <span class="nav-icon" aria-hidden="true">
                <span v-if="iconSvgs[node.item.CodigoOpcion]" v-html="iconSvgs[node.item.CodigoOpcion]" class="nav-icon-svg" />
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                  <circle cx="12" cy="12" r="9" />
                </svg>
              </span>
              <span class="nav-label">{{ node.item.Titulo }}</span>
            </button>
          </template>
        </nav>
      </aside>
    </div>
  </Teleport>

  <!-- Sidebar desktop -->
  <aside
    v-if="!isMobile"
    class="nav-sidebar"
    :class="{ 'nav-sidebar--collapsed': collapsed }"
    aria-label="Menú de navegación"
  >
    <!-- Header del sidebar -->
    <div class="nav-header">
      <span v-if="!collapsed" class="nav-title">{{ title || 'Portal QA' }}</span>
      <button
        class="nav-toggle-btn"
        :aria-label="collapsed ? 'Expandir menú' : 'Colapsar menú'"
        :title="collapsed ? 'Expandir' : 'Colapsar'"
        @click="toggleCollapse"
      >
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          :style="{ transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
    </div>

    <!-- Lista de navegación -->
    <nav class="nav-list" aria-label="Navegación principal">
      <template v-for="node in tree" :key="node.item.CodigoOpcion">
        <!-- Grupo con hijos -->
        <div v-if="node.children.length > 0" class="nav-group">
          <button
            class="nav-group-header"
            :class="{ 'nav-group-header--active': hasActiveChild(node) }"
            :title="node.item.Tooltip"
            @click="toggleGroup(node.item.CodigoOpcion)"
          >
            <span class="nav-icon" aria-hidden="true">
              <span v-if="iconSvgs[node.item.CodigoOpcion]" v-html="iconSvgs[node.item.CodigoOpcion]" class="nav-icon-svg" />
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <circle cx="12" cy="12" r="9" />
              </svg>
            </span>
            <template v-if="!collapsed">
              <span class="nav-label">{{ node.item.Titulo }}</span>
              <svg
                class="nav-chevron"
                :class="{ 'nav-chevron--open': isGroupOpen(node.item.CodigoOpcion) }"
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </template>
          </button>
          <div v-if="!collapsed" v-show="isGroupOpen(node.item.CodigoOpcion)" class="nav-children">
            <template v-for="child in node.children" :key="child.item.CodigoOpcion">
              <!-- Nietos -->
              <div v-if="child.children.length > 0" class="nav-group nav-group--nested">
                <button
                  class="nav-group-header nav-group-header--nested"
                  :class="{ 'nav-group-header--active': hasActiveChild(child) }"
                  :title="child.item.Tooltip"
                  @click="toggleGroup(child.item.CodigoOpcion)"
                >
                  <span class="nav-label nav-label--nested">{{ child.item.Titulo }}</span>
                  <svg
                    class="nav-chevron"
                    :class="{ 'nav-chevron--open': isGroupOpen(child.item.CodigoOpcion) }"
                    width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                <div v-show="isGroupOpen(child.item.CodigoOpcion)" class="nav-children nav-children--nested">
                  <button
                    v-for="grandchild in child.children"
                    :key="grandchild.item.CodigoOpcion"
                    class="nav-item nav-item--grandchild"
                    :class="{ 'nav-item--active': isActive(grandchild.item.Url) }"
                    :title="grandchild.item.Tooltip"
                    @click="handleNavigate(grandchild.item.Url)"
                  >
                    <span class="nav-label nav-label--nested">{{ grandchild.item.Titulo }}</span>
                  </button>
                </div>
              </div>
              <!-- Hijo hoja -->
              <button
                v-else
                class="nav-item nav-item--child"
                :class="{ 'nav-item--active': isActive(child.item.Url) }"
                :title="child.item.Tooltip"
                @click="handleNavigate(child.item.Url)"
              >
                <span class="nav-label nav-label--nested">{{ child.item.Titulo }}</span>
              </button>
            </template>
          </div>
        </div>
        <!-- Item raíz sin hijos -->
        <button
          v-else
          class="nav-item nav-item--root"
          :class="{ 'nav-item--active': isActive(node.item.Url) }"
          :title="node.item.Tooltip"
          @click="handleNavigate(node.item.Url)"
        >
          <span class="nav-icon" aria-hidden="true">
            <span v-if="iconSvgs[node.item.CodigoOpcion]" v-html="iconSvgs[node.item.CodigoOpcion]" class="nav-icon-svg" />
            <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
              <circle cx="12" cy="12" r="9" />
            </svg>
          </span>
          <span v-if="!collapsed" class="nav-label">{{ node.item.Titulo }}</span>
        </button>
      </template>
    </nav>
  </aside>
</template>

<style scoped>
/* ── SIDEBAR BASE ────────────────────────────────── */
.nav-sidebar {
  display: flex;
  flex-direction: column;
  width: 240px;
  min-height: 100%;
  background: #F8FAFC;
  border-right: 1px solid #E2E8F0;
  transition: width 0.25s ease;
  flex-shrink: 0;
}

.nav-sidebar--collapsed {
  width: 56px;
}

/* ── HEADER ──────────────────────────────────────── */
.nav-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 0.75rem;
  border-bottom: 1px solid #E2E8F0;
  min-height: 52px;
}

.nav-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1054A0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-toggle-btn {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  color: #64748B;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}

.nav-toggle-btn:hover {
  background: #E2E8F0;
  color: #1054A0;
}

.nav-close-btn {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: 0;
  background: transparent;
  color: #64748B;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

.nav-close-btn:hover {
  background: #E2E8F0;
  color: #1054A0;
}

/* ── NAV LIST ────────────────────────────────────── */
.nav-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* ── ITEMS ───────────────────────────────────────── */
.nav-item,
.nav-group-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  text-align: left;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #334155;
  background: transparent;
  transition: background 0.15s, color 0.15s;
}

.nav-item {
  padding: 0.55rem 0.65rem;
}

.nav-group-header {
  padding: 0.6rem 0.65rem;
  font-weight: 500;
}

.nav-item:hover,
.nav-group-header:hover {
  background: #EEF2FF;
  color: #1054A0;
}

.nav-item--active {
  background: #DBEAFE;
  color: #1054A0;
  font-weight: 600;
}

.nav-group-header--active {
  color: #1054A0;
}

/* ── ICON ────────────────────────────────────────── */
.nav-icon {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  color: #64748B;
}

.nav-icon-svg :deep(svg) {
  width: 100%;
  height: 100%;
}

/* ── LABEL ───────────────────────────────────────── */
.nav-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-label--nested {
  font-size: 0.82rem;
}

/* ── CHEVRON ─────────────────────────────────────── */
.nav-chevron {
  flex-shrink: 0;
  color: #94A3B8;
  transition: transform 0.2s;
  margin-left: auto;
}

.nav-chevron--open {
  transform: rotate(180deg);
}

/* ── CHILDREN ────────────────────────────────────── */
.nav-children {
  margin-top: 2px;
  padding-left: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.nav-children--nested {
  padding-left: 1rem;
}

.nav-item--child {
  padding: 0.45rem 0.65rem;
  font-size: 0.83rem;
}

.nav-item--grandchild {
  padding: 0.4rem 0.65rem;
  font-size: 0.8rem;
}

.nav-group--nested .nav-group-header--nested {
  padding: 0.45rem 0.65rem;
  font-size: 0.83rem;
  font-weight: 500;
}

/* ── MOBILE OVERLAY ──────────────────────────────── */
.nav-overlay {
  position: fixed;
  inset: 0;
  background: rgba(11, 37, 89, 0.4);
  backdrop-filter: blur(2px);
  z-index: 60;
  display: flex;
  justify-content: flex-start;
}

.nav-sidebar--mobile {
  width: min(280px, 88vw);
  height: 100%;
  border-right: none;
  box-shadow: 8px 0 32px rgba(0, 0, 0, 0.18);
  animation: slideInLeft 0.2s ease-out;
}

@keyframes slideInLeft {
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
}

/* ── HAMBURGER ───────────────────────────────────── */
.nav-hamburger {
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.nav-hamburger:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

/* ── SCROLLBAR ───────────────────────────────────── */
.nav-list::-webkit-scrollbar {
  width: 4px;
}

.nav-list::-webkit-scrollbar-track {
  background: transparent;
}

.nav-list::-webkit-scrollbar-thumb {
  background: #CBD5E1;
  border-radius: 4px;
}
</style>
